import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { format, startOfDay } from 'date-fns';
import DayNavigator from './DayNavigator';
import DoctorChips from './DoctorChips';
import WeekBlock from './WeekBlock';
import {
  WEEKDAYS_SHORT, countItems, countLabel, dayKey, groupByDay, isSameDay, itemKind, monthDoctors, monthTitle, releasedStatusIds, shortNames,
  weekDayLabel, weekOf,
} from './agendaDay';
import {
  HOUR_HEIGHT, SLOT_MINUTES, bandsByLane, blockBox, clipBands, dayWidth, hourRange, laneLayout, minuteTop, minutesOf, slotAt, slotState,
  weekColumns, weekMonths, weekTitle,
} from './weekGrid';
import { scrollPageTo, useMinuteTick } from './listShared';
import { useSyncedScroll } from './useSyncedScroll';
import './AgendaDayList.css';
import './AgendaWeek.css';

const NO_ITEMS = new Map();
const NO_COUNTS = new Map();
const hm = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const countText = (count) => (count ? `${count} ${count === 1 ? 'agendamento' : 'agendamentos'}` : 'nenhum agendamento');

// Largura da grade, que ocupa a tela toda: medida na própria área de rolagem. Antes da 1ª medida, a largura da página
function useWidth(ref, active) {
  const [width, setWidth] = useState(() => document.documentElement.clientWidth || 0);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!active || !element) return undefined;
    const measure = () => setWidth(element.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Mede no quadro seguinte: mudar o tamanho dentro do aviso do ResizeObserver dá o erro "ResizeObserver loop"
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [ref, active]);

  return width;
}

// Semana (aba de Horários, Agenda até 992 px): os horários na vertical e os dias em colunas, uma por doutor em cada dia.
// Só props, como a Lista do dia: os dados são o mês que a página já carrega (com as semanas inteiras das pontas), e abrir,
// criar, filtrar e atualizar ficam com a página. doctors: os doutores com os dias de atendimento (faixas, horas e fim de
// semana); bookable: os que podem receber agendamento (ativos, com horário, no filtro), que viram coluna mesmo sem
// agendamento no mês, para o toque no horário livre
const AgendaWeek = ({
  day, events, outOfHours = [], doctors = [], bookable = [], statusList, state, refreshError, filtered, scrollRequest, onScrollDone,
  returnFocus, chip = 'all', onChipChange, onSelectDay, onToday, onOpenDay, onOpen, onNew, onNewAt, onFilters, onRetry, onRefresh,
}) => {
  const ready = state === 'ready';
  const now = new Date();
  const today = startOfDay(now);

  const [announcement, setAnnouncement] = useState('');
  const [slot, setSlot] = useState(null); // horário livre marcado: { scope, key, doctorId, start }
  const [hint, setHint] = useState(null); // aviso do toque fora do horário: { text }
  const rootRef = useRef(null);
  const barRef = useRef(null);
  const headRef = useRef(null);
  const headScrollRef = useRef(null);
  const bodyRef = useRef(null);
  const fabRef = useRef(null);
  const headerHeight = useRef(46); // --agenda-header-h, lido do CSS ao montar

  const releasedIds = useMemo(() => releasedStatusIds(statusList), [statusList]);
  const byDay = useMemo(() => groupByDay(events), [events]);
  const monthDocs = useMemo(() => monthDoctors(events), [events]);
  const doctorsById = useMemo(() => new Map(doctors.map((doctor) => [doctor.id, doctor])), [doctors]);
  const bands = useMemo(() => bandsByLane(outOfHours), [outOfHours]);

  // Colunas: os doutores com agendamento no mês e os que podem receber agendamento, pelo nome. Chips só com mais de um
  // doutor no mês (o login de doutor só vê os dele), como no Diário
  const laneDoctors = useMemo(() => {
    const all = new Map(monthDocs.map((doctor) => [doctor.id, doctor]));
    bookable.forEach((doctor) => {
      if (!all.has(doctor.id)) all.set(doctor.id, { id: doctor.id, nome: doctor.nome ?? '' });
    });
    return [...all.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [monthDocs, bookable]);
  const names = useMemo(() => shortNames(laneDoctors), [laneDoctors]);
  const showChips = monthDocs.length > 1;
  const activeChip = showChips && monthDocs.some((doctor) => doctor.id === chip) ? chip : 'all';
  const lanes = activeChip === 'all' ? laneDoctors : laneDoctors.filter((doctor) => doctor.id === activeChip);
  const laneIds = lanes.map((doctor) => doctor.id);

  // Grade: carregando, dias e horas só pelos horários de atendimento (os agendamentos na memória são de outro mês ou filtro)
  const shownItems = ready ? byDay : NO_ITEMS;
  const days = weekColumns(day, shownItems, laneIds, doctorsById);
  const range = hourRange(days, shownItems, laneIds, doctorsById);
  const gridShown = state !== 'error';
  const width = useWidth(bodyRef, gridShown);
  const dayW = dayWidth(width, days.length, lanes.length);
  const laneW = lanes.length > 1 ? (dayW - (lanes.length - 1)) / lanes.length : dayW;
  const hours = [];
  for (let minutes = range.start; minutes < range.end; minutes += 60) hours.push(minutes);

  const todayShown = days.some((date) => isSameDay(date, today));
  const nowMinutes = minutesOf(now);
  const showNow = ready && todayShown && nowMinutes >= range.start && nowMinutes <= range.end;
  useMinuteTick(ready && todayShown);

  // Contagens: a semana inteira (dom–sáb). O título, dos doutores das colunas; os chips, de cada doutor
  const weekItems = ready ? weekOf(day).flatMap((date) => byDay.get(dayKey(date)) ?? []) : [];
  const counts = countItems(weekItems.filter((item) => laneIds.includes(item.tecnico?.id)), releasedIds);
  const chipCounts = new Map([
    ['all', countItems(weekItems, releasedIds).appointments],
    ...monthDocs.map((doctor) => [doctor.id, countItems(weekItems.filter((item) => item.tecnico?.id === doctor.id), releasedIds).appointments]),
  ]);
  const dayCount = (date) => countItems((byDay.get(dayKey(date)) ?? []).filter((item) => laneIds.includes(item.tecnico?.id)), releasedIds).appointments;
  const title = `Semana de ${weekTitle(days[0], days[days.length - 1], today)}`;

  // O marcador vale para a semana e as colunas em que foi feito: trocar de semana ou de chip o apaga (voltar não o traz)
  const scope = `${dayKey(days[0])}|${laneIds.join(',')}`;
  const marked = slot && slot.scope === scope ? slot : null;
  useEffect(() => {
    setSlot((current) => (current && current.scope !== scope ? null : current));
  }, [scope]);

  // Anuncia a troca de semana, o carregamento e os erros (a atualização de 30 s não é anunciada)
  const summary = `${title}: ${countLabel(counts)}`;
  useEffect(() => {
    if (state === 'error') setAnnouncement(`Não foi possível carregar a agenda de ${monthTitle(day, today)}.`);
    else if (state === 'loading') setAnnouncement('Carregando agendamentos…');
    else setAnnouncement(summary);
  }, [scope, state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (refreshError) setAnnouncement('Não foi possível atualizar.');
  }, [refreshError]);

  useEffect(() => {
    if (!hint) return undefined;
    const timer = setTimeout(() => setHint(null), 3000);
    return () => clearTimeout(timer);
  }, [hint]);

  useSyncedScroll(headScrollRef, bodyRef, gridShown);

  // Altura da barra da data (o cabeçalho dos dias gruda logo abaixo dela), sem redesenhar a Semana
  useLayoutEffect(() => {
    const root = rootRef.current;
    const bar = barRef.current;
    headerHeight.current = parseFloat(getComputedStyle(root).getPropertyValue('--agenda-header-h')) || 46;
    if (!bar) return undefined;
    const measure = () => root.style.setProperty('--agenda-bar-h', `${bar.offsetHeight || 58}px`);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  // Parte de cima que fica presa: cabeçalho do app, barra da data e (quando grudados) o cabeçalho dos dias
  const stuckHeight = (withDays) => {
    const bar = barRef.current;
    const head = headRef.current;
    const barHeight = bar && getComputedStyle(bar).position === 'sticky' ? bar.offsetHeight : 0;
    return headerHeight.current + barHeight + (withDays && head ? head.offsetHeight : 0);
  };

  // Tab e Shift+Tab: o bloco focado que ficou atrás da parte presa rola para baixo dela. Os controles da barra e o
  // cabeçalho dos dias ficam de fora: estão sempre à vista
  useEffect(() => {
    const root = rootRef.current;
    let frame = 0;
    const onFocusIn = (event) => {
      if (barRef.current?.contains(event.target) || headRef.current?.contains(event.target)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = event.target.getBoundingClientRect();
        if (!rect.height) return;
        const limit = stuckHeight(Boolean(bodyRef.current?.contains(event.target)));
        if (rect.top < limit) scrollPageTo(rect.top + window.scrollY - limit - 8);
      });
    };
    root.addEventListener('focusin', onFocusIn);
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener('focusin', onFocusIn);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Detalhes (ou novo) fechado: o foco volta ao bloco (ou ao +) se ficou perdido na página, como no Diário
  useEffect(() => {
    if (!returnFocus) return undefined;
    const tryFocus = () => {
      if (Date.now() > returnFocus.until) return;
      if (document.activeElement && document.activeElement !== document.body) return;
      const target = returnFocus.id ? rootRef.current?.querySelector(`.agenda-week-block[data-id="${returnFocus.id}"]`) : fabRef.current;
      target?.focus({ preventScroll: true });
    };
    const timers = [350, 1000].map((delay) => setTimeout(tryFocus, delay));
    return () => timers.forEach(clearTimeout);
  }, [returnFocus, events]);

  // Rolagem pedida pela página. Na lateral, o dia escolhido à vista ('keep' mantém a coluna: ‹ e › trocam de semana no mesmo
  // lugar). Na vertical: 'now' até a linha "agora" com uma hora de contexto acima (sem hoje na semana, como 'head'); 'head'
  // sobe até o título, se ele ficou para cima; 'keep' não mexe. Espera a semana carregada
  useLayoutEffect(() => {
    if (!scrollRequest || !ready) return;
    const body = bodyRef.current;
    if (body && scrollRequest.kind !== 'keep') {
      const index = Math.max(0, days.findIndex((date) => isSameDay(date, day)));
      body.scrollLeft = Math.max(0, Math.min(index * dayW, body.scrollWidth - body.clientWidth));
    }

    const pageTop = (element) => element.getBoundingClientRect().top + window.scrollY;
    const line = rootRef.current.querySelector('[data-now]');
    if (scrollRequest.kind === 'now' && line) {
      if (!scrollRequest.onlyIfTop || window.scrollY === 0) scrollPageTo(pageTop(line) - stuckHeight(true) - HOUR_HEIGHT);
    } else if (scrollRequest.kind !== 'keep') {
      const head = rootRef.current.querySelector('.agenda-day-head');
      const top = head ? pageTop(head) - stuckHeight(false) - 8 : 0;
      if (head && top < window.scrollY && (!scrollRequest.onlyIfTop || window.scrollY === 0)) scrollPageTo(top);
    }
    onScrollDone(scrollRequest);
  }, [scrollRequest, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toque no espaço vazio de uma coluna: marca o quarto de hora (o segundo toque, no marcador, abre o Novo). Na faixa
  // "Fora do horário", só o aviso. Blocos e o marcador tratam o próprio toque
  function tapLane(event, doctor, date) {
    if (!ready || event.target.closest('button')) return;
    const start = slotAt(event.clientY - event.currentTarget.getBoundingClientRect().top, range);
    if (start === null) return;
    const key = dayKey(date);
    const occupying = (byDay.get(key) ?? []).filter((item) => item.tecnico?.id === doctor.id && itemKind(item, releasedIds) !== 'released');
    const result = slotState(start, bands.get(`${doctor.id}|${key}`) ?? [], occupying);
    if (result === 'out-of-hours') {
      setSlot(null);
      setHint({ text: `${hm(start)} fica fora do horário de ${names.get(doctor.id)}.` });
    } else if (result === 'free') {
      setSlot({ scope, key, doctorId: doctor.id, start });
      setHint(null);
      setAnnouncement(`Horário livre marcado: ${hm(start)} com ${names.get(doctor.id)}. Toque de novo para agendar.`);
    }
  }

  const renderLane = (date, doctor) => {
    const key = dayKey(date);
    const items = ready ? (byDay.get(key) ?? []).filter((item) => item.tecnico?.id === doctor.id) : [];
    const laneBands = ready ? clipBands(bands.get(`${doctor.id}|${key}`) ?? [], range) : [];
    const markedHere = marked && marked.key === key && marked.doctorId === doctor.id;
    return (
      // O toque no espaço vazio é um atalho de toque e de mouse; pelo teclado, o + faz o mesmo
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div key={doctor.id} className='agenda-week-lane' role='group' aria-label={`${weekDayLabel(date)}, ${names.get(doctor.id)}`} onClick={(event) => tapLane(event, doctor, date)}>
        {laneBands.map(([from, to]) => (
          <div key={from} className='agenda-week-off' style={{ top: minuteTop(from, range), height: minuteTop(to, range) - minuteTop(from, range) }} />
        ))}
        {laneLayout(items).map(({ item, col, cols }) => {
          const box = blockBox(item, range);
          return (
            <WeekBlock
              key={item.id}
              item={item}
              kind={itemKind(item, releasedIds)}
              top={box.top}
              height={box.height}
              col={col}
              cols={cols}
              laneWidth={laneW}
              doctorName={names.get(doctor.id) || item.tecnico?.nome}
              onOpen={onOpen}
            />
          );
        })}
        {markedHere ? (
          <button
            type='button'
            className='agenda-week-slot'
            style={{ top: minuteTop(marked.start, range) + 1, height: minuteTop(marked.start + SLOT_MINUTES, range) - minuteTop(marked.start, range) - 2 }}
            aria-label={`Novo agendamento às ${hm(marked.start)} com ${names.get(doctor.id)}, ${weekDayLabel(date)}`}
            onClick={() => {
              setSlot(null);
              onNewAt(doctor, date, marked.start);
            }}
          >
            + {hm(marked.start)}
          </button>
        ) : ''}
      </div>
    );
  };

  let notice = null;
  if (state === 'error') {
    notice = (
      <div className='agenda-day-empty'>
        <p>Não foi possível carregar a agenda de {monthTitle(day, today)}.</p>
        <Button type='button' onClick={onRetry}>Tentar de novo</Button>
      </div>
    );
  } else if (ready && !weekItems.some((item) => laneIds.includes(item.tecnico?.id))) {
    const chipDoctor = activeChip === 'all' ? null : lanes[0];
    if (chipDoctor && weekItems.length) {
      notice = (
        <div className='agenda-day-empty is-inline'>
          <p>Nenhum agendamento de {names.get(chipDoctor.id)} nesta semana.</p>
          <Button type='button' variant='outline-primary' onClick={() => onChipChange('all')}>Ver todos</Button>
        </div>
      );
    } else if (filtered) {
      notice = (
        <div className='agenda-day-empty is-inline'>
          <p>Nenhum agendamento nesta semana com os filtros aplicados.</p>
          <Button type='button' variant='dark' onClick={onFilters}>Filtros</Button>
        </div>
      );
    } else {
      notice = (
        <div className='agenda-day-empty is-inline'>
          <p>Nenhum agendamento nesta semana.</p>
          <Button type='button' onClick={() => onNew(chipDoctor)}>+ Novo agendamento</Button>
        </div>
      );
    }
  }

  const gridStyle = {
    '--week-day-w': `${dayW}px`,
    '--week-days': days.length,
    '--week-lanes': Math.max(1, lanes.length),
    '--week-grid-h': `${minuteTop(range.end, range)}px`,
  };

  return (
    <section className='agenda-day agenda-week' ref={rootRef} aria-label='Semana'>
      <DayNavigator
        mode='week'
        day={day}
        today={today}
        counts={NO_COUNTS}
        onSelect={(date, source) => onSelectDay(date, source === 'step' ? 'keep' : undefined)}
        onToday={onToday}
        barRef={barRef}
      />

      {refreshError && ready ? (
        <div className='agenda-day-banner'>
          <span>Não foi possível atualizar.</span>
          <Button type='button' variant='outline-dark' size='sm' onClick={onRefresh}>Tentar de novo</Button>
        </div>
      ) : ''}

      <div className='agenda-day-head'>
        <h2>{title}</h2>
        {ready ? <p className='agenda-day-count'>{countLabel(counts)}</p> : ''}
      </div>

      {ready && showChips ? <DoctorChips doctors={monthDocs} names={names} active={activeChip} counts={chipCounts} onChange={onChipChange} /> : ''}

      {notice}

      {gridShown ? (
        <div className={`agenda-week-grid${ready ? '' : ' is-loading'}`} style={gridStyle} aria-busy={!ready}>
          <div className='agenda-week-headbox' ref={headRef}>
            <div className='agenda-week-corner' aria-hidden='true'>{weekMonths(days)}</div>
            <div className='agenda-week-hscroll' ref={headScrollRef}>
              <div className='agenda-week-htrack'>
                {days.map((date) => {
                  const count = ready ? dayCount(date) : undefined;
                  const isToday = isSameDay(date, today);
                  const selected = isSameDay(date, day);
                  const label = `Abrir ${weekDayLabel(date)} no Diário${count === undefined ? '' : `, ${countText(count)}`}${isToday ? ', hoje' : ''}`;
                  return (
                    <div key={dayKey(date)} className='agenda-week-hday'>
                      <button
                        type='button'
                        className={`agenda-week-day${selected ? ' is-selected' : ''}`}
                        aria-current={isToday ? 'date' : undefined}
                        aria-label={label}
                        onClick={() => onOpenDay(date)}
                      >
                        <span aria-hidden='true'>{WEEKDAYS_SHORT[date.getDay()]}</span>
                        <span className='agenda-week-day-number' aria-hidden='true'>{date.getDate()}</span>
                        <span className='agenda-week-day-count' aria-hidden='true'>{count === undefined ? '' : (count || '–')}</span>
                      </button>
                      {lanes.length > 1 ? (
                        <div className='agenda-week-lanes' aria-hidden='true'>
                          {lanes.map((doctor) => <span key={doctor.id}>{names.get(doctor.id)}</span>)}
                        </div>
                      ) : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className='agenda-week-body' ref={bodyRef}>
            <div className='agenda-week-track'>
              <div className='agenda-week-gutter' aria-hidden='true'>
                {hours.map((minutes) => <span key={minutes} className='agenda-week-hour' style={{ top: minuteTop(minutes, range) }}>{hm(minutes)}</span>)}
                {showNow ? <span className='agenda-week-now-tag' style={{ top: minuteTop(nowMinutes, range) }}>{format(now, 'HH:mm')}</span> : ''}
              </div>
              {days.map((date) => {
                const isToday = isSameDay(date, today);
                return (
                  <div key={dayKey(date)} className={`agenda-week-col${isToday ? ' is-today' : ''}`}>
                    {lanes.map((doctor) => renderLane(date, doctor))}
                    {showNow && isToday ? <div className='agenda-week-now' data-now='' style={{ top: minuteTop(nowMinutes, range) }} aria-hidden='true' /> : ''}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : ''}

      {gridShown && !ready ? <p className='agenda-day-loading'>Carregando agendamentos…</p> : ''}

      <button type='button' className='agenda-day-fab' ref={fabRef} aria-label='Novo agendamento' title='Novo agendamento' onClick={() => onNew(activeChip === 'all' ? null : lanes[0])}>
        <FaPlus aria-hidden='true' />
      </button>

      {hint ? <p className='agenda-week-hint' role='status'>{hint.text}</p> : ''}
      <p className='visually-hidden' aria-live='polite'>{announcement}</p>
    </section>
  );
};

export default AgendaWeek;
